import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Api } from 'lavva.exalushome';
import { IRcz21ConfigService } from 'lavva.exalushome.extalife/build/js/Devices/DeviceServices/FloodSensor/IRcz21ConfigService';
import {
  FloodSensorAlertMode,
  Rcz21ConfigParams,
} from 'lavva.exalushome.extalife/build/js/Devices/DeviceServices/FloodSensor/Rcz21ConfigParams';
import { Rcz21ConfigService } from 'lavva.exalushome.extalife/build/js/Devices/DeviceServices/FloodSensor/Rcz21ConfigService';
import { ExtaLifeService } from 'lavva.exalushome.extalife/build/js/ExtaLife';
import { IDevice } from 'lavva.exalushome/build/js/Services/Devices/IDevice';
import { IDeviceChannel } from 'lavva.exalushome/build/js/Services/Devices/IDeviceChannel';
import { Checkbox, InputSelect, SubmitButton } from '../../../../../../components';
import ArrowButton from '../../../../../../components/arrow-button';
import { useBackdropContext } from '../../../../../../context/backdrop';
import ConfigCustomPage from '../../../device-details/components/config-custom';
import ConfigurationInput from '../../../device-details/components/configuration-input';
import { useConfigurationService } from '../../../device-details/hooks/use-configuration-service';
import { useDeviceUpdates } from '../../hooks/use-device-updates';

type Rcz21ConfigFormProps = {
  device: IDevice;
  channel?: IDeviceChannel;
};

const Rcz21ConfigForm: React.FC<Rcz21ConfigFormProps> = ({ device, channel }) => {
  const { t } = useTranslation('device-info');
  const [open, setOpen] = useState<boolean>(false);
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();
  const {
    handleGetParamsResponse,
    handleSubmitResponse,
    firmwareService,
    DiagnosticButton,
    UserManualButton,
    DiagnosticResultPopup,
  } = useConfigurationService(device);
  const { VersionDeviceButton, CheckUpdateDeviceButton } = useDeviceUpdates(device);
  const form = useForm<Rcz21ConfigParams>();

  const { AlertMode, RepeatFloodEvent } = form.watch();

  const getDefaultParams = async () => {
    if (channel) {
      turnOnBackdrop();
      const extaLifeService = Api.Get<ExtaLifeService>(ExtaLifeService.ServiceName);
      const configService = await extaLifeService.GetDeviceServiceByServiceTypeAsync<IRcz21ConfigService>(
        Rcz21ConfigService.ServiceName,
      );

      const data = await configService.GetAllParamsAsync(device, channel.Number);
      turnOffBackdrop();

      handleGetParamsResponse<Rcz21ConfigParams>(data, () => {
        Object.entries(data as Rcz21ConfigParams).forEach(([name, value]) => {
          form.setValue(name as keyof Rcz21ConfigParams, value);
        });
        setOpen(true);
      });
    }
  };

  const onSubmit = form.handleSubmit(async (values) => {
    if (channel) {
      turnOnBackdrop();
      let params = new Rcz21ConfigParams();
      params = { ...params, ...values };

      const extaLifeService = Api.Get<ExtaLifeService>(ExtaLifeService.ServiceName);
      const configService = await extaLifeService.GetDeviceServiceByServiceTypeAsync<IRcz21ConfigService>(
        Rcz21ConfigService.ServiceName,
      );

      const result = await configService.SetAllParamsAsync(params, channel.Number);
      handleSubmitResponse(result);
      turnOffBackdrop();
    }
  });

  const onChangeAlertMode = (value: FloodSensorAlertMode) => form.setValue('AlertMode', value);

  const handleOpen = () => getDefaultParams();

  const handleBack = () => setOpen(false);

  return (
    <>
      {open ? (
        <ConfigCustomPage handleBack={handleBack}>
          <FormProvider {...form}>
            <form onSubmit={onSubmit} className="grid-list-24">
              <InputSelect
                onChange={onChangeAlertMode}
                value={AlertMode}
                options={[
                  { label: t('exalus.params.Rcz21Params.alertModes.None'), value: FloodSensorAlertMode.None },
                  { label: t('exalus.params.Rcz21Params.alertModes.Alarm'), value: FloodSensorAlertMode.Alarm },
                  { label: t('exalus.params.Rcz21Params.alertModes.Led'), value: FloodSensorAlertMode.Led },
                  {
                    label: t('exalus.params.Rcz21Params.alertModes.AlarmAndLed'),
                    value: FloodSensorAlertMode.AlarmAndLed,
                  },
                ]}
                label={t('exalus.params.Rcz21Params.AlertMode')}
              />
              <Checkbox
                checked={RepeatFloodEvent}
                onChange={(e) => {
                  form.setValue('RepeatFloodEvent', e.target.checked);
                }}
                reverse
              >
                {t('exalus.params.Rcz21Params.RepeatFloodEvent')}
              </Checkbox>
              <ConfigurationInput
                name="RepeatFloodEventTime"
                label={t('exalus.params.Rcz21Params.RepeatFloodEventTime')}
                placeholder={t('exalus.params.Rcz21Params.RepeatFloodEventTime')}
                inputType="number"
                min={10}
                max={900}
              />
              <ConfigurationInput
                name="SyncTime"
                label={t('exalus.params.Rcz21Params.SyncTime')}
                placeholder={t('exalus.params.Rcz21Params.SyncTime')}
                inputType="number"
              />
              <SubmitButton />
            </form>
          </FormProvider>
        </ConfigCustomPage>
      ) : (
        <>
          {channel ? (
            <>
              <ArrowButton title={t('exalus.moreConfiguration')} onClick={() => handleOpen()} className="m-b-24" />
            </>
          ) : (
            <>
              {firmwareService && VersionDeviceButton}
              {firmwareService && CheckUpdateDeviceButton}
              {DiagnosticButton}
              {UserManualButton}
            </>
          )}
        </>
      )}
      {DiagnosticResultPopup}
    </>
  );
};

export default Rcz21ConfigForm;
